import { cn } from './cn'

export interface FbCheckboxProps {
  checked?: boolean
  indeterminate?: boolean
  disabled?: boolean
  onChange?: (checked: boolean) => void
  className?: string
}

type CheckboxState = 'unchecked' | 'checked' | 'indeterminate' | 'disabled'

const stateStyles: Record<CheckboxState, string> = {
  unchecked:
    'bg-[#FAFAFA] border border-[#C4C4C4] hover:bg-[#F7FAF9] hover:border-[#087443]',
  checked:
    'bg-[#EEF5F2] border border-[#087443] hover:bg-[#F7FAF9] hover:border-[#087443]',
  indeterminate:
    'bg-[#EEF5F2] border border-[#087443] hover:bg-[#F7FAF9] hover:border-[#087443]',
  disabled: 'bg-[#FAFAFA] border border-[#E2E2E2] cursor-not-allowed',
}

const ICON_COLOR_ACTIVE = '#087443'
const ICON_COLOR_DISABLED = '#E2E2E2'

export function FbCheckbox({
  checked = false,
  indeterminate = false,
  disabled = false,
  onChange,
  className,
}: FbCheckboxProps) {
  const handleClick = () => {
    if (disabled) return
    onChange?.(!checked)
  }

  const isChecked = checked && !indeterminate

  // Derive single state for styling
  const state: CheckboxState = disabled
    ? 'disabled'
    : indeterminate
      ? 'indeterminate'
      : checked
        ? 'checked'
        : 'unchecked'

  const iconColor = disabled ? ICON_COLOR_DISABLED : ICON_COLOR_ACTIVE

  return (
    <div
      role="checkbox"
      aria-checked={indeterminate ? 'mixed' : checked}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault()
          handleClick()
        }
      }}
      className={cn(
        'w-4 h-4 rounded-sm flex items-center justify-center cursor-pointer transition-colors box-border',
        stateStyles[state],
        className
      )}
    >
      {isChecked && (
        <svg
          width="10"
          height="8"
          viewBox="0 0 10 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="flex-shrink-0"
        >
          <path
            d="M1 4L3.5 6.5L9 1"
            stroke={iconColor}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      {indeterminate && (
        <svg
          width="8"
          height="2"
          viewBox="0 0 8 2"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="flex-shrink-0"
        >
          <path
            d="M1 1H7"
            stroke={iconColor}
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      )}
    </div>
  )
}

export default FbCheckbox
